import React, { useState, useEffect, useRef } from "react";
import { X, Search } from "lucide-react";
import { Task } from "../../types";
import { searchTasks, SearchResult } from "../../lib/utils";
import SearchResultCard from "../task/SearchResultCard";

interface SearchDialogProps {
  tasks: Task[];
  onClose: () => void;
  onSelectTask: (task: Task) => void;
  initialSearchTerm?: string;
  onSearchTermChange?: (term: string) => void;
}

const SearchDialog: React.FC<SearchDialogProps> = ({
  tasks,
  onClose,
  onSelectTask,
  initialSearchTerm = "",
  onSearchTermChange,
}) => {
  const [inputValue, setInputValue] = useState(initialSearchTerm);
  const [debouncedTerm, setDebouncedTerm] = useState(initialSearchTerm);
  const [results, setResults] = useState<SearchResult[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTerm(inputValue);
    }, 200);

    return () => clearTimeout(timer);
  }, [inputValue]);

  // Perform search when debounced term changes
  useEffect(() => {
    if (debouncedTerm.trim()) {
      const searchResults = searchTasks(tasks, debouncedTerm);
      setResults(searchResults);
    } else {
      setResults([]);
    }

    // Update parent's search term
    onSearchTermChange?.(debouncedTerm);
  }, [debouncedTerm, tasks, onSearchTermChange]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "Enter" && results.length > 0) {
        onSelectTask(results[0].task);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, onSelectTask, results]);

  // Autofocus input
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleResultClick = (task: Task) => {
    onSelectTask(task);
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-30 p-2 sm:p-4 animate-dialog-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div role="dialog" aria-label="Search Tasks" className="bg-warm-bg-card rounded-xl border border-warm-border w-full max-w-2xl max-h-[95vh] sm:max-h-[80vh] flex flex-col animate-dialog-panel">
        {/* Search Input */}
        <div className="p-3 sm:p-4 border-b border-warm-border">
          <div className="flex items-center gap-2 sm:gap-3">
            <Search className="text-warm-text-tertiary flex-shrink-0" size={20} />
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Search tasks..."
              className="flex-1 min-w-0 bg-transparent text-base sm:text-lg text-warm-text-primary placeholder:text-warm-text-tertiary focus:outline-none"
            />
            <button
              onClick={onClose}
              aria-label="Close search"
              className="text-warm-text-tertiary hover:text-warm-text-secondary transition-colors flex-shrink-0 p-1 -m-1"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Results Area */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4">
          {!debouncedTerm.trim() ? (
            // Empty state
            <div className="flex flex-col items-center justify-center py-12 text-warm-text-tertiary">
              <Search size={48} className="mb-4 opacity-50" />
              <p className="text-sm">Start typing to search tasks...</p>
            </div>
          ) : results.length === 0 ? (
            // No results state
            <div className="flex flex-col items-center justify-center py-12 text-warm-text-tertiary">
              <Search size={48} className="mb-4 opacity-50" />
              <p className="text-sm">No tasks match '{debouncedTerm}'</p>
            </div>
          ) : (
            // Results
            <div className="space-y-4">
              <div className="text-sm text-warm-text-secondary mb-3">
                {results.length} {results.length === 1 ? "result" : "results"} found
              </div>
              <div className="space-y-3">
                {results.map((result, index) => (
                  <div
                    key={result.task.id}
                    className="animate-fade-in-up"
                    style={{ animationDelay: `${Math.min(index * 50, 300)}ms` }}
                  >
                    <SearchResultCard
                      task={result.task}
                      searchTerm={debouncedTerm}
                      onClick={() => handleResultClick(result.task)}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchDialog;
